import { TypeSystem } from "../../support/type-system.support";
import { ResellerPayment } from "./reseller-payment.model";

	/**
	 * @description
	 *  Modelo de una transacción de revendedor.
	 *
	 * @see gigot-backend/Models/Users/ResellerStatement.cs
	 */

export class ResellerTransaction {

	public campaignId: number;
	public dueAmount: number;
	public dueDate: Date;
	public firstDebtApplied: boolean;
	public id: number;
	public invoiceDate: Date;
	public invoiceNumber: string;
	public lateFeeAmount: number;
	public orderId: number;
	public paidAmount: number;
	public resellerPayments: ResellerPayment[];
	public resellerStatementId: number;
	public resolved: boolean;
	public secondDebtApplied: boolean;

	/**
	 * @description
	 *	Construye una transacción con una semántica vacía. Nótese que no es lo
	 *	mismo que construir una transacción con valores por defecto. Las
	 *	reglas generales de construcción son:
	 *
	 *      - Campos 'array' se definen como [].
	 *      - Campos 'boolean' se definen como 'false'.
	 *      - Campos 'Date' se definen como 'undefined'.
	 *      - Campos 'enum' se definen como 'undefined'.
	 *      - Campos 'number' se definen como 'undefined'.
	 *      - Campos 'string' se definen como '' (cadena vacía).
	 *      - Campos 'object' se definen llamando a su propio método #empty().
	 *      - Campos de la superclase se definen al igual que campos 'object'.
	 *      - Campos genéricos se definen como 'undefined'.
	 *		- Campos recursivos se definen como 'undefined'.
	 *
	 * @returns
	 *	Un transacción vacía.
	 */
	public static empty(): ResellerTransaction {
		const resellerTransaction: ResellerTransaction = new ResellerTransaction();
		resellerTransaction.campaignId = undefined;
		resellerTransaction.dueAmount = undefined;
		resellerTransaction.dueDate = undefined;
		resellerTransaction.firstDebtApplied = false;
		resellerTransaction.id = undefined;
		resellerTransaction.invoiceDate = undefined;
		resellerTransaction.invoiceNumber = "";
		resellerTransaction.lateFeeAmount = undefined;
		resellerTransaction.orderId = undefined;
		resellerTransaction.paidAmount = undefined;
		resellerTransaction.resellerPayments = [];
		resellerTransaction.resellerStatementId = undefined;
		resellerTransaction.resolved = false;
		resellerTransaction.secondDebtApplied = false;
		return resellerTransaction;
	}

	/**
	 * @description
	 *  Método de reificación de un modelo compatible anémico. Esto permite
	 *  ejecutar lógica de negocios sobre el modelo generado, siendo que el
	 *  valor de sus propiedades se mantiene.
	 *
	 * @param anemicResellerTransaction
	 *  El modelo anémico a reificar.
	 *
	 * @returns
	 *  Devuelve un modelo de esta clase, cuyas propiedades fueron copiadas en
	 *  profundidad desde el modelo anémico.
	 */
	public static from(anemicResellerTransaction: ResellerTransaction): ResellerTransaction {
		if (!anemicResellerTransaction) return undefined;
		const resellerTransaction: ResellerTransaction = ResellerTransaction.empty();
		resellerTransaction.campaignId = anemicResellerTransaction.campaignId;
		resellerTransaction.dueAmount = anemicResellerTransaction.dueAmount;
		resellerTransaction.dueDate = new Date(anemicResellerTransaction.dueDate);
		resellerTransaction.firstDebtApplied = anemicResellerTransaction.firstDebtApplied;
		resellerTransaction.id = anemicResellerTransaction.id;
		resellerTransaction.invoiceDate = new Date(anemicResellerTransaction.invoiceDate);
		resellerTransaction.invoiceNumber = anemicResellerTransaction.invoiceNumber;
		resellerTransaction.lateFeeAmount = anemicResellerTransaction.lateFeeAmount;
		resellerTransaction.orderId = anemicResellerTransaction.orderId;
		resellerTransaction.paidAmount = anemicResellerTransaction.paidAmount;
		resellerTransaction.resellerPayments = TypeSystem.orElse(anemicResellerTransaction.resellerPayments, [])
			.map(resellerPayment => ResellerPayment.from(resellerPayment));
		resellerTransaction.resellerStatementId = anemicResellerTransaction.resellerStatementId;
		resellerTransaction.resolved = anemicResellerTransaction.resolved;
		resellerTransaction.secondDebtApplied = anemicResellerTransaction.secondDebtApplied;
		return resellerTransaction;
	}
}
